export function isChinese(text: string) {
  if (!text) return false;
  // 匹配中文字符的正则表达式
  const chineseRegex = /[\u4e00-\u9fa5]/g;

  // 匹配英文单词的正则表达式
  const englishWordRegex = /\b[A-Za-z]+\b/g;

  // 统计中文字符数
  const chineseCount = (text.match(chineseRegex) || []).length;

  // 将文本按空格分割为单词数组，然后统计英文单词数
  const wordsArray = text.split(/\s+/);
  const englishWordCount = wordsArray.filter((word) =>
    word.match(englishWordRegex),
  ).length;

  return chineseCount > englishWordCount;
}

export function convertAbstractToMarkdownAdvanced(
  abstractText: string,
): string {
  const lines = abstractText.split('\n');

  let markdown = '';
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    if (trimmed.startsWith('```')) {
      const block = [trimmed];
      index += 1;
      while (index < lines.length) {
        block.push(lines[index]);
        if (lines[index].trim().startsWith('```')) {
          index += 1;
          break;
        }
        index += 1;
      }
      markdown += `${block.join('\n')}\n\n`;
      continue;
    }

    if (isMarkdownTableStart(lines, index)) {
      const block = [];
      while (index < lines.length && isMarkdownTableLine(lines[index])) {
        block.push(lines[index].trim());
        index += 1;
      }
      markdown += `${block.join('\n')}\n\n`;
      continue;
    }

    if (hasInlinePythonCode(line)) {
      const converted = convertInlinePythonCodeBlocks(line);
      if (converted) {
        markdown += converted;
      }
      index += 1;
      continue;
    }

    // 检测以大写字母或中文字符开头，后跟冒号的模式
    const isHeader = /^([A-Z\s]+|[\u4e00-\u9fa5]+)[:：]/.test(trimmed);

    if (isHeader) {
      // 提取标题和内容
      const regex = /^([^:：]+)[:：](.*)$/;
      const match = line.match(regex);

      if (match) {
        const header = match[1].trim();
        const content = match[2].trim();

        // 添加标题为H4级别
        markdown += `\n#### ${header}\n\n`;
        // 添加内容（如果有）
        if (content) {
          markdown += `${content}\n\n`;
        }
      } else {
        // 如果正则匹配失败，将整行作为普通内容处理
        markdown += `${line.trim()}\n\n`;
      }
    } else {
      // 添加内容到当前段落
      if (trimmed !== '') {
        markdown += `${trimmed}\n\n`;
      }
    }

    index += 1;
  }

  return markdown.trim();
}

function isMarkdownTableStart(lines: string[], index: number) {
  return (
    isMarkdownTableLine(lines[index]) &&
    index + 1 < lines.length &&
    /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(
      lines[index + 1],
    )
  );
}

function isMarkdownTableLine(line: string) {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|');
}

function hasInlinePythonCode(line: string) {
  return /\b(?:Python\s+)?class\s+[A-Za-z_]\w*:/.test(line);
}

function convertInlinePythonCodeBlocks(line: string) {
  let rest = line;
  let result = '';
  const codeStartPattern = /\b(?:Python\s+)?class\s+[A-Za-z_]\w*:/;

  while (rest.trim()) {
    const match = rest.match(codeStartPattern);
    if (match?.index === undefined) {
      result += `${rest.trim()}\n\n`;
      break;
    }

    const prefix = rest.slice(0, match.index).trim();
    if (prefix) {
      result += `${prefix}\n\n`;
    }

    const codeAndTail = rest.slice(match.index);
    const codeEnd = findInlinePythonCodeEnd(codeAndTail);
    const code = codeAndTail.slice(0, codeEnd).trim();
    rest = codeAndTail.slice(codeEnd);

    if (code) {
      result += `\`\`\`python\n${formatInlinePythonCode(code)}\n\`\`\`\n\n`;
    }
  }

  return result;
}

function findInlinePythonCodeEnd(text: string) {
  const stagePattern =
    /\s+(?:مرحله\s*[۰-۹0-9]+|步骤\s*[0-9]+|Step\s*[0-9]+)[:：]/g;
  const match = stagePattern.exec(text);
  return match?.index ?? text.length;
}

function formatInlinePythonCode(codeText: string) {
  return codeText
    .trim()
    .replace(/^Python\s+(?=class\s+)/, '')
    .replace(/\s+(def\s+[A-Za-z_]\w*\([^)]*\):)/g, '\n    $1')
    .replace(/\s+(self\.[A-Za-z_]\w*\s*=)/g, '\n        $1')
    .replace(/\s+([A-Za-z_]\w*\s*=\s*)/g, '\n        $1')
    .replace(/\s+(return\s+)/g, '\n        $1')
    .replace(/\s+(#[^\n]+?)(?=\s+(?:def|self\.|[A-Za-z_]\w*\s*=|return|print\(|$))/g, ' $1')
    .replace(/\s+(print\()/g, '\n$1')
    .trim();
}

export const maskApiKey = (key: string) => {
  if (key.length <= 8) return key; // 如果密钥太短则不脱敏
  const prefix = key.substring(0, 7);
  const maskedLength = key.length - 7;
  const maskedPart = '*'.repeat(maskedLength);
  return `${prefix}${maskedPart}`;
};

export const isEmptyObject = (obj: object) => {
  return Object.getOwnPropertyNames(obj).length === 0;
};
